import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { companiesAPI, officesAPI, rolePermissionsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Buildings, Plus, PencilSimple, Trash, Shield, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  director: 'Director',
  supervisor: 'Supervisor',
  admin: 'Office Admin',
  scheduler: 'Scheduler',
  caregiver: 'Caregiver',
  client: 'Client / Family',
};

const PAGE_LABELS = {
  dashboard: 'Dashboard',
  clients: 'Clients',
  caregivers: 'Caregivers',
  schedule: 'Schedule',
  'open-shifts': 'Open Shifts',
  'visit-logs': 'Visit Logs',
  clock: 'Clock In/Out',
  messages: 'Messages',
  forms: 'Forms',
  'ai-assistant': 'AI Assistant',
  'audit-logs': 'Audit Logs',
  offices: 'Offices',
  billing: 'Billing',
  payroll: 'Payroll',
  invoices: 'Invoices',
  reports: 'Reports',
  holidays: 'Holiday Billing',
  branding: 'Invoice Branding',
};

const ROLE_ORDER = ['super_admin', 'director', 'supervisor', 'admin', 'scheduler', 'caregiver', 'client'];
const PAGE_ORDER = Object.keys(PAGE_LABELS);

const RolesPermissionsPage = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState('');
  const [matrix, setMatrix] = useState({}); // role → {page: bool}
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);

  // Company dialog
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: '', phone: '', address: '' });

  const canManage = user?.role === 'super_admin' || user?.role === 'director';

  useEffect(() => { load(); }, []);
  useEffect(() => { if (selectedOfficeId) loadMatrix(); }, [selectedOfficeId]);

  const load = async () => {
    setLoading(true);
    try {
      const [cs, os] = await Promise.all([
        companiesAPI.getAll().catch(() => ({ data: [] })),
        officesAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setCompanies(cs.data || []);
      setOffices(os.data || []);
      if ((os.data || []).length > 0 && !selectedOfficeId) {
        setSelectedOfficeId(user?.office_id || os.data[0].id);
      }
    } catch (e) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const loadMatrix = async () => {
    try {
      const res = await rolePermissionsAPI.get(selectedOfficeId);
      setMatrix(res.data.roles || {});
    } catch (e) {
      toast.error('Failed to load permissions matrix');
    }
  };

  const togglePerm = (role, page) => {
    setMatrix(prev => ({
      ...prev,
      [role]: { ...prev[role], [page]: !prev[role]?.[page] }
    }));
  };

  const saveRow = async (role) => {
    setSavingRole(role);
    try {
      await rolePermissionsAPI.update(role, matrix[role] || {}, selectedOfficeId);
      toast.success(`Permissions saved for ${ROLE_LABELS[role]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingRole(null); }
  };

  // Companies handlers
  const openCreateCompany = () => {
    setEditingCompany(null);
    setCompanyForm({ name: '', phone: '', address: '' });
    setCompanyDialogOpen(true);
  };
  const openEditCompany = (c) => {
    setEditingCompany(c);
    setCompanyForm({ name: c.name, phone: c.phone || '', address: c.address || '' });
    setCompanyDialogOpen(true);
  };
  const saveCompany = async () => {
    if (!companyForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editingCompany) {
        await companiesAPI.update(editingCompany.id, companyForm);
        toast.success('Company updated');
      } else {
        await companiesAPI.create(companyForm);
        toast.success('Company created');
      }
      setCompanyDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };
  const deleteCompany = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"? This is only allowed if no offices are linked.`)) return;
    try {
      await companiesAPI.delete(c.id);
      toast.success('Company deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="roles-permissions-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-semibold flex items-center gap-2">
          <Shield weight="duotone" className="w-7 h-7 text-primary" />
          Roles &amp; Permissions
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage the Company → Office hierarchy and control which pages each role can see, per office.
        </p>
      </div>

      <Tabs defaultValue="permissions" className="space-y-6">
        <TabsList className="bg-muted/50 rounded-xl p-1">
          <TabsTrigger value="permissions" className="rounded-lg" data-testid="tab-permissions">Permissions Matrix</TabsTrigger>
          {user?.role === 'super_admin' && (
            <TabsTrigger value="companies" className="rounded-lg" data-testid="tab-companies">Companies</TabsTrigger>
          )}
        </TabsList>

        {/* PERMISSIONS MATRIX */}
        <TabsContent value="permissions" className="space-y-4">
          <Card className="rounded-2xl border border-border">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="font-heading text-lg">Page Access by Role</CardTitle>
                  <CardDescription>Toggle which pages each role can see. Overrides are per-office.</CardDescription>
                </div>
                <div className="min-w-[220px]">
                  <Label className="text-xs">Office</Label>
                  <Select value={selectedOfficeId} onValueChange={setSelectedOfficeId}>
                    <SelectTrigger className="rounded-xl" data-testid="office-select">
                      <SelectValue placeholder="Select office" />
                    </SelectTrigger>
                    <SelectContent>
                      {offices.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="permissions-matrix">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-2 sticky left-0 bg-background z-10 min-w-[130px]">Page</th>
                      {ROLE_ORDER.map(r => (
                        <th key={r} className="text-center py-2 px-2 min-w-[80px] text-xs font-semibold">{ROLE_LABELS[r]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PAGE_ORDER.map(page => (
                      <tr key={page} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 pr-2 font-medium sticky left-0 bg-background z-10">{PAGE_LABELS[page]}</td>
                        {ROLE_ORDER.map(role => {
                          const on = !!matrix[role]?.[page];
                          const disabled = !canManage || (role === 'super_admin');
                          return (
                            <td key={role} className="text-center py-1">
                              <button
                                type="button"
                                disabled={disabled}
                                onClick={() => togglePerm(role, page)}
                                className={`w-7 h-7 rounded-md inline-flex items-center justify-center transition-colors ${
                                  on ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-50 text-red-500 hover:bg-red-100'
                                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                                data-testid={`perm-${role}-${page}`}
                                title={`${ROLE_LABELS[role]} · ${PAGE_LABELS[page]} · ${on ? 'ON' : 'OFF'}`}
                              >
                                {on ? <CheckCircle weight="bold" className="w-4 h-4" /> : <XCircle weight="bold" className="w-4 h-4" />}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr className="border-t-2 border-border">
                      <td className="py-3 pr-2 font-semibold text-xs uppercase tracking-wider text-muted-foreground sticky left-0 bg-background">Save row</td>
                      {ROLE_ORDER.map(r => (
                        <td key={r} className="text-center py-3 px-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-lg h-7 text-[11px]"
                            disabled={!canManage || r === 'super_admin' || savingRole === r}
                            onClick={() => saveRow(r)}
                            data-testid={`save-perms-${r}`}
                          >
                            {savingRole === r ? '...' : 'Save'}
                          </Button>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Green = access granted · Red = hidden. Super Admin is always full access.
                Changes take effect on the user&apos;s next page load.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPANIES */}
        {user?.role === 'super_admin' && (
          <TabsContent value="companies" className="space-y-4">
            <Card className="rounded-2xl border border-border">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle className="font-heading text-lg">Companies</CardTitle>
                    <CardDescription>A Company owns one or more Offices. Directors have visibility across all offices in their company.</CardDescription>
                  </div>
                  <Button className="rounded-xl gap-2" onClick={openCreateCompany} data-testid="new-company-btn">
                    <Plus weight="bold" className="w-4 h-4" /> New Company
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <Buildings weight="duotone" className="w-14 h-14 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No companies yet. Create one to group multiple offices.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {companies.map(c => (
                      <div key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`company-row-${c.id}`}>
                        <div>
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {c.phone || 'No phone'} · <Badge variant="secondary" className="rounded-full text-[10px]">{c.office_count} offices</Badge>
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => openEditCompany(c)} data-testid={`edit-company-${c.id}`}>
                            <PencilSimple weight="bold" className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive" onClick={() => deleteCompany(c)} data-testid={`delete-company-${c.id}`}>
                            <Trash weight="bold" className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingCompany ? 'Edit Company' : 'New Company'}</DialogTitle>
            <DialogDescription>A company groups multiple offices under one Director.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={companyForm.name} onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })} className="rounded-xl" data-testid="company-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={companyForm.phone} onChange={e => setCompanyForm({ ...companyForm, phone: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={companyForm.address} onChange={e => setCompanyForm({ ...companyForm, address: e.target.value })} className="rounded-xl" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setCompanyDialogOpen(false)}>Cancel</Button>
              <Button className="flex-1 rounded-xl" onClick={saveCompany} data-testid="save-company-btn">
                {editingCompany ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissionsPage;
